<script setup lang="ts">
/**
 * What went wrong with a source's refresh, in words, with the next step.
 *
 * "Last run failed · 8 days ago" was true and useless: it did not say which
 * run, whether it still mattered, or what to do. This says which job and
 * when, what that kind of job's failure means (a scheduled refresh retries
 * on its own; an upload-triggered one affects the uploads in that run), how
 * it clears, and links to the errors from that exact run.
 */
import { computed } from "vue";
import type { DataSource, JobRow } from "../api/admin";
import { fmtAge, fmtDateTime } from "../utils/datetime";
import { adminLink } from "../utils/vocabulary";

const props = defineProps<{ source: DataSource }>();

const failing = computed<JobRow[]>(() =>
  props.source.jobs.filter(
    (j) =>
      j.trigger_type !== "Manual" &&
      (j.failure_open ?? j.last_status.toLowerCase() === "failed"),
  ),
);
const late = computed<JobRow[]>(() =>
  props.source.jobs.filter((j) => j.overdue),
);

function failedAt(j: JobRow): string {
  return j.failed_execution?.start || j.last_start;
}

function logsLink(j: JobRow): string {
  const run = j.failed_execution?.name || j.name;
  return adminLink("logs", { contains: run, level: "ERROR", since: "-30d" });
}

function meaning(j: JobRow): string {
  if (j.trigger_type === "Event")
    return "This job runs when something arrives (an upload, a change). A failure affects what was in that run — usually one file — not the whole source. It clears when the next run succeeds.";
  return "This is a scheduled refresh; it tries again on its own at the next scheduled time. If that fails too, the errors say why. Until then the data is as fresh as the last successful run.";
}
</script>

<template>
  <div v-if="failing.length || late.length" class="health">
    <h5>What happened</h5>
    <div v-for="j in late" :key="`late:${j.name}`" class="health__item">
      <p>
        <strong>{{ j.name }}</strong> has not run when expected —
        {{ j.reason }}.
      </p>
      <p class="muted">
        A job that never starts writes no logs, so there is no error to read:
        check that the job still exists and is enabled in Azure, or ask whoever
        runs the deployment.
      </p>
    </div>
    <div v-for="j in failing" :key="`fail:${j.name}`" class="health__item">
      <p>
        <strong>{{ j.name }}</strong> failed
        <span :title="fmtDateTime(failedAt(j))">{{ fmtAge(failedAt(j)) }}</span
        >, and nothing has succeeded since.
      </p>
      <p class="muted">{{ meaning(j) }}</p>
      <a :href="logsLink(j)">Show the errors from that run →</a>
    </div>
  </div>
</template>

<style scoped>
.health {
  margin: 0.75rem 0 0;
  padding: 0.6rem 0.8rem;
  border: 1px solid var(--color-warn);
  border-radius: var(--radius-md);
  background: var(--color-warn-bg);
  font-size: 0.86rem;
}
.health h5 {
  margin: 0 0 0.3rem;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-warn-ink);
}
.health__item + .health__item {
  margin-top: 0.6rem;
}
.health p {
  margin: 0 0 0.25rem;
}
.health a {
  color: var(--color-accent);
}
.muted {
  color: var(--color-muted);
}
</style>
